import { Button, Flex, Heading, Text } from '@radix-ui/themes';
import { TbAlertCircle, TbCircleCheck, TbInfoCircle, TbX } from 'react-icons/tb';
import { toast as sonnerToast } from 'sonner';
import { useEffect, useState } from 'react';
import styles from './toast.module.scss';
interface ToastProps {
  id: string | number;
  icon?: React.ReactNode;
  title: string;
  description?: string;
  type: 'error' | 'success' | 'warning' | 'info';
  cancelButton?: {
    label: string;
    onClick?: () => void;
  };
  confirmButton?: {
    label: string;
    onClick: () => void;
  };
  duration?: number;
  countdown?: boolean;
  onCountdownComplete?: () => void;
  countdownDuration?: number;
}

const toastIconMap = {
  error: <TbAlertCircle fontSize="2rem" color="var(--red-11)" />,
  success: <TbCircleCheck fontSize="2rem" color="var(--green-11)" />,
  warning: <TbAlertCircle fontSize="2rem" color="var(--yellow-11)" />,
  info: <TbInfoCircle fontSize="2rem" color="var(--cyan-11)" />,
};

const toastBorderMap = {
  error: 'var(--red-8)',
  success: 'var(--green-8)',
  warning: 'var(--yellow-8)',
  info: 'var(--cyan-8)',
};

export function toast(toast: Omit<ToastProps, 'id'>) {
  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        title={toast.title}
        description={toast.description}
        type={toast.type}
        icon={toast.icon === null ? undefined : toastIconMap[toast.type]}
        cancelButton={toast.cancelButton}
        confirmButton={toast.confirmButton}
        duration={toast.duration}
        countdown={toast.countdown}
        onCountdownComplete={toast.onCountdownComplete}
      />
    ),
    {
      duration: toast.duration || 3000,
    }
  );
}

/** A fully custom toast that still maintains the animations and interactions. */
export function Toast(props: ToastProps) {
  const {
    title,
    description,
    cancelButton,
    confirmButton,
    type,
    id,
    icon,
    countdown,
    onCountdownComplete,
    countdownDuration = 3,
  } = props;
  const [countdownValue, setCountdownValue] = useState(countdownDuration);
  const [displayDescription, setDisplayDescription] = useState(description);

  useEffect(() => {
    if (countdown && countdownValue && description) {
      setDisplayDescription(`${description} ${countdownValue}`);

      if (countdownValue > 1) {
        const timer = setTimeout(() => {
          setCountdownValue((prev) => (prev ?? 0) - 1);
        }, 1000);

        return () => clearTimeout(timer);
      } else if (countdownValue === 1) {
        sonnerToast.dismiss(id);
        onCountdownComplete?.();
      }
    }
  }, [countdown, description, countdownValue, onCountdownComplete, id, countdownDuration]);

  const handleConfirmationClick = () => {
    if (confirmButton?.onClick) {
      confirmButton.onClick();
    }
    sonnerToast.dismiss(id);
  };

  const handleCancelClick = () => {
    if (cancelButton?.onClick) {
      cancelButton.onClick();
    }
    sonnerToast.dismiss(id);
  };
  return (
    <Flex
      justify="between"
      align="center"
      style={{
        backgroundColor: 'var(--gray-2)',
        width: '100%',
        border: '1px solid var(--gray-8)',
        borderRadius: '6px',
        position: 'relative',
        minWidth: '35rem',
        boxShadow: '0 0 3px 2px var(--gray-2)',
        borderLeft: `4px solid ${toastBorderMap[type]}`,
      }}
      data-type={type}
      p="4"
    >
      <Flex
        align="center"
        justify="center"
        className={styles.ToastCloseButton}
        role="button"
        onClick={() => sonnerToast.dismiss(id)}
      >
        <TbX fontSize="1rem" />
      </Flex>
      <Flex direction="column" justify="center" gap="1">
        <Flex align="center" gap={icon ? '2' : '0'}>
          {icon && (
            <Flex justify="center" align="center">
              {icon}
            </Flex>
          )}
          <Heading size="2">{title}</Heading>
        </Flex>
        {displayDescription && <Text size="2">{displayDescription}</Text>}
      </Flex>
      <Flex gap="2">
        {cancelButton?.label && (
          <Button size="1" variant="soft" onClick={handleCancelClick}>
            {cancelButton.label}
          </Button>
        )}
        {confirmButton?.label && (
          <Button size="1" onClick={handleConfirmationClick}>
            {confirmButton.label}
          </Button>
        )}
      </Flex>
    </Flex>
  );
}
